import React, { useCallback, useEffect, useState } from 'react';

import PageHeader from '../../components/PageHeader.jsx';
import Loader, { ErrorBox } from '../../components/Loader.jsx';
import { BusinessDefaultsAPI } from '../../lib/api';
import { notify } from '../../lib/notifications';


/**
 * Business → Default tab.
 *
 * Surfaces the **system-defaults inspector** that lets an org admin
 * verify and (re-)provision the fixtures every brand-new tenant needs
 * for the rest of the platform to function.  Without these, broad
 * swathes of the app silently break:
 *
 *   * **Default Roles** (`admin`, `employee`) — the legacy tenant
 *     signup form looks up `Role.objects.get(name='employee')`; if the
 *     row is missing, signup raises a 500.
 *
 *   * **Default Permissions** (`Tasks`, `Complaints`, `Routine-Tasks`)
 *     — the per-app `@is_authorized` gate returns *deny* by default,
 *     so without these rows wired into role-permission bindings every
 *     tenant request is rejected.
 *
 *   * **Default Divisions** (`Auretics`, `Product`, `Person`,
 *     `Supply`, `Department`, `Admin`, `Other`, `Public`) — the
 *     public-complaint flow hard-fails when the `Public` division is
 *     missing.
 *
 *   * **External Users (Auretics SSO support infrastructure)** — the
 *     `support@auretics.com` user, the `auretics-users` role, the
 *     `Complaints` permission with full CRUD action flags, the role
 *     binding, the per-user override row and the `Auretics` division.
 *     This is what `complaints_management_system.views
 *     .ensure_support_user` provisions every time an external Auretics
 *     user lands on the SSO endpoint; pre-seeding it here means the
 *     very first SSO click is fast (no idempotent writes) AND the org
 *     admin can verify the support infrastructure is healthy before
 *     customers start hitting it.
 *
 * Backend contract — see `organisational_admin/api_defaults.py`:
 *
 *   * `GET  /api/admin/defaults/` returns the per-fixture status.
 *   * `POST /api/admin/defaults/seed/` seeds one group or all.
 *
 * Both calls are tenant-scoped and require the caller to be an org
 * admin (or super-admin) — see `_is_org_admin` server-side.
 */
export default function BusinessDefaults() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // ``busy`` carries the key currently being seeded (or "all"); used
  // to show a spinner on the matching button and disable the others
  // so the admin can't fire two seeds in parallel.
  const [busy, setBusy] = useState(null);

  // ---- Loaders ---------------------------------------------------
  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await BusinessDefaultsAPI.status();
      setData(payload);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // ---- Seed actions ----------------------------------------------
  const seed = useCallback(async (key) => {
    setBusy(key);
    setError(null);
    try {
      const payload = await BusinessDefaultsAPI.seed(key);
      setData(payload);
      const label = key === 'all' ? 'All defaults' : payload.groups.find((g) => g.key === key)?.label || key;
      notify.success(`${label} provisioned successfully.`);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  }, []);

  if (loading && !data) return <Loader label="Inspecting defaults…" />;

  return (
    <>
      <PageHeader
        title="🛠️ System Defaults"
        subtitle={
          'Inspect and provision the fixtures every tenant needs. ' +
          'Re-runs are idempotent — safe to click as often as needed.'
        }
        actions={
          <>
            <button
              type="button"
              className="ty-btn ty-btn--ghost"
              onClick={reload}
              disabled={loading || !!busy}
              title="Re-fetch the current status without writing anything"
            >
              ↻ Refresh
            </button>
            <button
              type="button"
              className="ty-btn"
              onClick={() => seed('all')}
              disabled={!!busy || (data && data.all_ok)}
              title={
                data && data.all_ok
                  ? 'Everything is already provisioned'
                  : 'Idempotently seed every default group'
              }
            >
              {busy === 'all' ? 'Seeding…' : '✨ Seed everything'}
            </button>
          </>
        }
      />

      <ErrorBox error={error} />

      {data && (
        <>
          <OverviewBanner data={data} />

          <div
            className="ty-stats"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))' }}
          >
            {data.groups.map((group) => (
              <DefaultGroupCard
                key={group.key}
                group={group}
                busy={busy === group.key || busy === 'all'}
                disabled={!!busy}
                onSeed={() => seed(group.key)}
              />
            ))}
          </div>

          <ContextualHelp />
        </>
      )}
    </>
  );
}


// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

/**
 * Top-of-page summary banner — green when every group is OK, amber
 * otherwise.  The wording deliberately calls out *why* the admin
 * should care (signup, SSO, public complaints) so they can make an
 * informed decision instead of just clicking buttons.
 */
function OverviewBanner({ data }) {
  const ok = data.all_ok;
  const totalMissing = data.groups.reduce(
    (sum, g) => sum + g.items.filter((i) => !i.present).length, 0,
  );
  const colour = ok ? '#5fd788' : '#f9771d';
  const bg     = ok ? 'rgba(95, 215, 136, 0.10)' : 'rgba(249, 119, 29, 0.10)';
  const title  = ok
    ? 'All defaults are provisioned.'
    : `${totalMissing} default${totalMissing === 1 ? '' : 's'} missing.`;
  const help = ok
    ? (
      'Self-service signup, the @is_authorized gate, public-complaint creation '
      + 'and the Auretics SSO flow will all work as expected for '
      + (data.organisation?.name || 'this organisation') + '.'
    )
    : (
      'Some platform features depend on the defaults below. '
      + 'Missing fixtures will cause signup failures, permission denials, '
      + 'public-complaint errors and slow SSO logins until they are seeded.'
    );

  return (
    <div
      className="ty-card"
      style={{
        borderLeft: `4px solid ${colour}`,
        background: bg,
        marginBottom: 16,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span aria-hidden style={{ fontSize: 20 }}>{ok ? '✅' : '⚠️'}</span>
        <strong style={{ color: colour }}>{title}</strong>
        {data.organisation?.name && (
          <span className="ty-muted" style={{ fontSize: 13 }}>
            · {data.organisation.name} ({data.organisation.schema_name})
          </span>
        )}
      </div>
      <p style={{ marginTop: 6, marginBottom: 0, color: '#31394f' }}>{help}</p>
    </div>
  );
}


/**
 * One card per default-group.  Renders:
 *   * Header: icon + label + “OK” / “Missing N” pill.
 *   * Description text from the API payload.
 *   * Per-fixture checklist (✓ green / ✗ red).
 *   * Action button — seed-only when something is missing,
 *     “re-provision” when everything is already present.
 */
function DefaultGroupCard({ group, busy, disabled, onSeed }) {
  const missing = group.items.filter((i) => !i.present).length;
  const allOk   = missing === 0;
  const accent  = allOk ? '#5fd788' : '#f9771d';

  return (
    <div className="ty-card" style={{ borderLeft: `4px solid ${accent}` }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span aria-hidden style={{ fontSize: 22 }}>{group.icon}</span>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#31394f' }}>
          {group.label}
        </h3>
        <span
          className="ty-muted"
          style={{
            marginLeft: 'auto',
            fontSize: 12,
            fontWeight: 700,
            padding: '2px 8px',
            borderRadius: 999,
            background: allOk ? 'rgba(95,215,136,0.15)' : 'rgba(249,119,29,0.15)',
            color: accent,
          }}
        >
          {allOk ? 'OK' : `${missing} missing`}
        </span>
      </div>

      <p style={{ margin: '8px 0 12px', color: '#5b6175', fontSize: 13, lineHeight: 1.5 }}>
        {group.description}
      </p>

      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {group.items.map((item) => (
          <li
            key={item.name}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 0',
              borderTop: '1px dashed rgba(31, 41, 55, 0.08)',
              fontSize: 13,
              color: item.present ? '#31394f' : '#7a3a1a',
            }}
          >
            <span aria-hidden style={{ fontSize: 14 }}>
              {item.present ? '✅' : '⛔'}
            </span>
            <span style={{ flex: 1 }}>{item.name}</span>
            <span
              className="ty-muted"
              style={{ fontSize: 11, fontWeight: 600, color: item.present ? '#5fd788' : '#f9771d' }}
            >
              {item.present ? 'Present' : 'Missing'}
            </span>
          </li>
        ))}
      </ul>

      <div className="ty-row ty-row--end" style={{ marginTop: 12 }}>
        <button
          type="button"
          className="ty-btn"
          onClick={onSeed}
          disabled={disabled}
          title={
            allOk
              ? 'Re-run the seeder — idempotent, safe to click.'
              : 'Provision the missing fixtures in this group.'
          }
        >
          {busy
            ? 'Seeding…'
            : allOk
              ? '↻ Re-provision'
              : `Seed ${group.label.toLowerCase()}`}
        </button>
      </div>
    </div>
  );
}


/**
 * Long-form context block at the bottom of the page.  It lives outside
 * the cards so it can reference all groups together without cluttering
 * each card.  Mirrors the rationale comment at the top of
 * ``organisational_admin/api_defaults.py`` so the SPA stays
 * documentation-equivalent with the backend.
 */
function ContextualHelp() {
  return (
    <div
      className="ty-card"
      style={{ marginTop: 16, background: 'rgba(121, 128, 255, 0.06)', borderLeft: '4px solid #7980ff' }}
    >
      <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#31394f' }}>
        Why these defaults matter
      </h4>
      <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: '#5b6175', fontSize: 13, lineHeight: 1.6 }}>
        <li>
          <strong>Roles</strong> — self-service signup attaches the
          <code> employee</code> role to every new user; if it doesn't
          exist, the form raises a 500.
        </li>
        <li>
          <strong>Permissions</strong> — the per-app
          <code> @is_authorized</code> gate denies by default, so the
          baseline rows must exist before role-permission bindings can
          authorise users for Tasks, Complaints and Routine-Tasks.
        </li>
        <li>
          <strong>Divisions</strong> — the public-complaint flow routes
          to the <code>Public</code> division by name; missing rows
          surface as a hard error on submission.
        </li>
        <li>
          <strong>External Users</strong> — Auretics users land on
          <code> /complaints/sso/login/</code>; without the support
          user, role, permission and Auretics division, the very first
          SSO click silently does idempotent writes — and any failure
          (e.g. duplicate email collision) breaks the SSO experience.
          Pre-seeding here removes the foot-gun.
        </li>
      </ul>
    </div>
  );
}
